import {Component} from '@megaphonenpm/mpui/src/Component.js';
import {div} from '@megaphonenpm/mputils/src/DOM.js';
import {get} from 'lodash-es';
import {copyTextToClipboard} from '@megaphonenpm/mputils/src/DOM.js';
import {MegaScreen} from '../MegaScreen.js';

/**
 * Displays a tooltip containing design information for the element under the mouse pointer.
 * The Element Tooltip is intended for use in theme production only, and is not visible unless
 * explicitly enabled in deployment settings.
 */
export class ElementTooltip extends Component {
  constructor () {
    super();
    this.element = null;
    this.pinned = false;
    this.lastPointerX = 0;
    this.lastPointerY= 0;

    document.addEventListener('mousemove', e => {
      this.lastPointerX = e.clientX;
      this.lastPointerY = e.clientY;

      if (!this.pinned) {
        this.setPointerPosition(e.clientX, e.clientY);
        if (get(MegaScreen.globals.dataStore.deploymentSettings, 'productiontools_enabled', false)) {
          this.view.classList.add('visible');
        } else {
          this.view.classList.remove('visible');
        }
      }
    });
  }

  build () {
    this.view = div(null, 'elementTooltipContainer');
    this.copyToClipboardNotification = div('copyToClipboardNotification');
    this.copyToClipboardNotification = div('copyToClipboardNotification');
    this.pinStatus = div('pinStatus');
    this.pinStatus.textContent = 'Press . to pin';
    this.view.append(this.copyToClipboardNotification);
    this.view.append(this.pinStatus);
    this.elementTooltip = div('elementTooltip');
    this.view.append(this.elementTooltip);

    this.copyToClipboardNotification.addEventListener('transitionend', e => {
      if (e.target === this.copyToClipboardNotification && e.propertyName === 'opacity') {
        this.copyToClipboardNotification.classList.remove('visible');
      }
    })

    this.initHotkeys();
  }

  initHotkeys () {
    MegaScreen.globals.mousetrap.bind('1', () => {
      this.copyToClipboard(this.element.clientWidth);
    });
    MegaScreen.globals.mousetrap.bind('2', () => {
      this.copyToClipboard(this.element.clientHeight);
    });
    MegaScreen.globals.mousetrap.bind('3', () => {
      this.copyToClipboard(this.elementStyle.fontSize);
    });
    MegaScreen.globals.mousetrap.bind('4', () => {
      this.copyToClipboard(this.elementStyle.fontFamily);
    });
    MegaScreen.globals.mousetrap.bind('5', () => {
      this.copyToClipboard(this.elementStyle.color);
    });
    MegaScreen.globals.mousetrap.bind('6', () => {
      this.copyToClipboard(this.elementStyle.backgroundImage === 'none' ? this.elementStyle.backgroundColor : this.elementStyle.backgroundImage);
    });
    MegaScreen.globals.mousetrap.bind('7', () => {
      this.copyToClipboard(this.elementStyle.margin);
    });
    MegaScreen.globals.mousetrap.bind('8', () => {
      this.copyToClipboard(this.elementStyle.padding);
    });
    MegaScreen.globals.mousetrap.bind('9', () => {
      this.copyToClipboard(this.elementStyle.border);
    });
    MegaScreen.globals.mousetrap.bind('0', () => {
      this.copyToClipboard(this.mediaX);
    });
    MegaScreen.globals.mousetrap.bind('-', () => {
      this.copyToClipboard(this.mediaY);
    });
    MegaScreen.globals.mousetrap.bind('.', () => {
      this.pinned = !this.pinned;
      if (this.pinned) {
        this.pinStatus.textContent = 'Press . to unpin';
      } else {
        this.setPointerPosition(this.lastPointerX, this.lastPointerY);
        this.pinStatus.textContent = 'Press . to pin';
      }
    });
  }

  copyToClipboard (text) {
    copyTextToClipboard(text);
    this.copyToClipboardNotification.innerHTML = `
    <span class="copyIcon">&#x2702;</span> Copied <span class="copiedValue">${text}</span> to clipboard
    `;
    this.copyToClipboardNotification.classList.add('visible');
  }

  setPointerPosition (x, y) {
    this.setElement(document.elementFromPoint(x, y));
    this.view.style.left = `${Math.min(Math.max(10, x - this.view.clientWidth/2), document.documentElement.clientWidth - this.view.clientWidth - 10) }px`;
    this.view.style.top = `${y - this.view.clientHeight - 40}px`;
  }

  setElement (el) {
    if (el !== this.element) {
      this.element = el;
      this.update();
    }
  }

  update () {
    this.elementStyle = getComputedStyle(this.element);
    let html = `
<div class="titleRow">
<div class="elementID">${this.element.id}</div>
<div><span class="hotkeyHint">1</span>${this.element.clientWidth} x <span class="hotkeyHint">2</span>${this.element.clientHeight}</div>
</div>`;

    if (Array.from(this.element.childNodes).some(child => child.nodeType === child.TEXT_NODE && child.textContent.trim().length > 0)) {
      html += `<div class="row"><span class="hotkeyHint">3</span>${this.elementStyle.fontSize}<span class="hotkeyHint">4</span>${this.elementStyle.fontFamily}<span class="hotkeyHint">5</span><span class="colorchip" style="background: ${this.elementStyle.color}"></span>${this.formatRGB(this.elementStyle.color)}</div>`;
    }

    // Special case for Sponsor and Show media:
    // When calculating the media's position, remove the ThemeSet's position. The resulting media
    // position can then be entered directly into Theme Designer.
    if (this.element.id.startsWith('sponsorMedia') || this.element.id.startsWith('showMedia')) {
      let currentThemeSet = MegaScreen.globals.themeService.getCurrentThemeSet();
      let mediaViewStyle = getComputedStyle(this.element.parentElement.parentElement);
      this.mediaX = parseInt(mediaViewStyle.left) - get(currentThemeSet, 'layout.screen.position.x', 0);
      this.mediaY = parseInt(mediaViewStyle.top) - get(currentThemeSet, 'layout.screen.position.y', 0);
      html += `<div class="row">Position: <span class="hotkeyHint">0</span>x: ${this.mediaX} <span class="hotkeyHint">-</span>y: ${this.mediaY}</div>`;
    } else {
      this.mediaX = 0;
      this.mediaY = 0;
    }

    if (this.elementStyle.margin !== '0px') {
      html += `<div class="row"><span class="hotkeyHint">7</span>Margin: ${this.elementStyle.margin}</div>`;
    }
    if (this.elementStyle.padding !== '0px') {
      html += `<div class="row"><span class="hotkeyHint">8</span>Padding: ${this.elementStyle.padding}</div>`;
    }
    if (this.elementStyle.borderStyle !== 'none') {
      html += `<div class="row"><span class="hotkeyHint">9</span>Border: ${this.elementStyle.border}</div>`;
    }
    if (this.elementStyle.backgroundImage !== 'none') {
      html += `<div class="row"><span class="hotkeyHint">6</span>Background:<span class="colorchip" style="background: ${this.elementStyle.backgroundImage}"></span>${this.elementStyle.backgroundImage}</div>`;
    } else {
      if (this.elementStyle.backgroundColor !== 'rgb(0, 0, 0)' && this.elementStyle.backgroundColor !== 'rgba(0, 0, 0, 0)') {
        html += `<div class="row"><span class="hotkeyHint">6</span>Background:<span class="colorchip" style="background: ${this.elementStyle.background}"></span>${this.formatRGB(this.elementStyle.backgroundColor)}</div>`;
      }
    }

    this.elementTooltip.innerHTML = html;
  }

  formatRGB (rgbstring) {
    return rgbstring.substring(rgbstring.indexOf('(') + 1, rgbstring.indexOf(')'));
  }
}
